import React from "react";
import Select, {
  components,
  DropdownIndicatorProps,
  OptionProps,
  SingleValueProps,
  StylesConfig,
} from "react-select";
import { Controller, Control } from "react-hook-form";
import { Typography, Box } from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

interface OptionType {
  value: number;
  label: string;
  img?: string;
  icon?: React.ElementType;
}

interface InputSelectProps {
  control: Control<any>;
  name: string;
  label: string;
  options: OptionType[];
  errors?: Record<string, any>;
  trigger: (name?: string | string[]) => void;
  placeholder: string;
}

const customStyles = (error?: boolean): StylesConfig<any, false> => ({
  indicatorSeparator: () => ({
    display: "none",
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    div: {
      padding: "6px 8px",
    },
  }),
  option: (provided, state) => ({
    ...provided,
    display: "flex",
    alignItems: "center",
    fontStyle: "normal",
    fontWeight: 500,
    fontSize: 16,
    lineHeight: "24px",
    letterSpacing: "0.015em",
    color: "var(--eerie-black)",
    padding: "14px 15px",
    cursor: state.isDisabled ? "not-allowed" : "pointer",
    background:
      state.isSelected || state.isFocused
        ? "var(--cimigo-blue-light-4)"
        : "#FFFFFF",
  }),
  placeholder: (provided, state) => ({
    ...provided,
    fontSize: 16,
    fontWeight: 500,
    color: state.isFocused ? "var(--cimigo-blue-light-1)" : "var(--gray-90)",
    whiteSpace: "nowrap",
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "5px 0 5px 16px",
    div: {
      paddingBottom: 0,
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    display: "flex",
    fontSize: 16,
    fontWeight: 500,
    alignItems: "center",
  }),
  control: (provided, state) => ({
    ...provided,

    cursor: state.isDisabled ? "not-allowed" : "pointer",
    background: state.isDisabled ? "var(--gray-5)" : "#FFFFFF",
    border: error ? "1px solid var(--cimigo-danger)" : state.isFocused
      ? "1px solid var(--cimigo-blue-light-1)"
      : "1px solid var(--gray-40)",
    boxShadow: "0",
    svg: {
      color: state.isFocused ? "var(--cimigo-blue-light-1)" : "var(--gray-60)",
    },
    div: {
      color: state.isFocused ? "var(--cimigo-blue-light-1)" : "var(--gray-90)",
    },
    "&:hover": {
      border: "1px solid var(--cimigo-blue-light-1)",
    },
  }),
});

const DropdownIndicator = (props: DropdownIndicatorProps<any>) => {
  return (
    <components.DropdownIndicator {...props}>
      {props.selectProps.menuIsOpen ? (
        <KeyboardArrowUpIcon />
      ) : (
        <KeyboardArrowDownIcon />
      )}
    </components.DropdownIndicator>
  );
};

const Option = ({ children, ...props }: OptionProps<any>) => (
  <components.Option {...props}>
    {props.data?.img && (
      <img
        src={props.data.img}
        alt=""
        style={{ marginRight: 8, width: 20, height: 20 }}
      />
    )}
    {props.data?.icon && <props.data.icon style={{ marginRight: 8 }} />}
    {children}
  </components.Option>
);

const SingleValue = ({ children, ...props }: SingleValueProps<any>) => (
  <components.SingleValue {...props}>
    {props.data?.img && (
      <img
        src={props.data.img}
        alt=""
        style={{ marginRight: 8, width: 20, height: 20 }}
      />
    )}
    {props.data?.icon && <props.data.icon style={{ marginRight: 8 }} />}
    {children}
  </components.SingleValue>
);

const InputSelect: React.FC<InputSelectProps> = ({
  control,
  name,
  label,
  options,
  errors,
  trigger,
  placeholder,
}) => {
  return (
    <Box>
      {label && (
        <Typography sx={{
           color: errors?.[name]?.message ? "var(--cimigo-danger)" : "var(--gray-80)",
          fontFamily: "Montserrat",
          fontSize:  "14px",
          fontWeight: "500",
          lineHeight:  "24px",
          letterSpacing: "0.015em",
          margin: 0,
        }} >{label}</Typography>
      )}

      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <Select
            {...field}
            options={options}
            isSearchable
            placeholder={placeholder}
            className="w-full max-h-10 font-medium"
            value={
              options.find((option) => option.value === field.value?.value) || null
            }
            onChange={(selectedOption) => {
      
              field.onChange(selectedOption);
              trigger(name);
            }}
            styles={customStyles(!!errors?.[name])}
            components={{ DropdownIndicator, Option, SingleValue }}
          />
        )}
      />
      {errors?.[name]?.message && (
        <p className="m-0 text-[14px] text-error">{errors[name].message}</p>
      )}
    </Box>
  );
};

export default InputSelect;
